import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

const StudentTopicProgress = () => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [classId, setClassId] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState({ topicName: "", remark: "" });

  // 🔑 values from sessionStorage
  const employeeId = Number(sessionStorage.getItem("employeeId"));
  const sectionId = Number(sessionStorage.getItem("sectionId"));
  const token = sessionStorage.getItem("token");

  // Handle opening modal with remark
  const handleViewRemark = (topicName, remark) => {
    setSelectedRemark({ topicName, remark });
    setOpenModal(true);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRemark({ topicName: "", remark: "" });
  };

  /* ================= FETCH SUBJECTS ================= */
  useEffect(() => {
    const fetchSubjects = async () => {
      setLoadingSubjects(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/Result/GetClassSubjects_Result`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              studentId: employeeId,
            }),
          }
        );

        const data = await res.json();
        console.log("✅ Subjects API:", data);

        if (Array.isArray(data)) {
          setSubjects(data);
          if (data.length > 0) {
            setClassId(data[0].classId); // same class for all subjects
          }
        } else {
          setSubjects([]);
        }
      } catch (error) {
        console.error("❌ Subjects error:", error);
        setSubjects([]);
      } finally {
        setLoadingSubjects(false);
      }
    };

    if (employeeId) fetchSubjects();
  }, [employeeId, token]);

  /* ================= FETCH TOPICS ================= */
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedSubjectId || !classId) return;

      setLoadingTopics(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              classId: classId,
              sectionId: sectionId,
              subjectId: selectedSubjectId,
            }),
          }
        );

        const data = await res.json();
        console.log("✅ Topics API:", data);

        const topicList = Array.isArray(data)
          ? data
          : Array.isArray(data.topics)
          ? data.topics
          : [];

        setTopics(topicList);
      } catch (error) {
        console.error("❌ Topics error:", error);
        setTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    };

    fetchTopics();
  }, [selectedSubjectId, classId, sectionId, token]);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      {/* ================= HEADER ================= */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #080F1F 0%, #102A43 55%, #1B4965 100%)",
          color: "#fff",
          p: 2.5,
          borderRadius: "8px",
          mb: 1,
          boxShadow: 3,
        }}
      >
        <Typography fontSize="26px" fontWeight="bold">
          Student Topic Progress
        </Typography>
      </Box>

      {/* ================= MAIN CARD ================= */}
      <Paper sx={{ p: 4, borderRadius: "12px" }}>
        {/* SUBJECT DROPDOWN */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubjectId}
                label="Subject"
                onChange={(e) => setSelectedSubjectId(e.target.value)}
              >
                {subjects.map((sub) => (
                  <MenuItem key={sub.subjectId} value={sub.subjectId}>
                    {sub.subjectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* LOADER */}
        {(loadingSubjects || loadingTopics) && (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {/* EMPTY MESSAGE */}
        {!selectedSubjectId && !loadingSubjects && (
          <Typography align="center" color="text.secondary" py={4}>
            Please select a subject to view topic progress
          </Typography>
        )}

        {/* ================= TOPICS TABLE ================= */}
        {!loadingTopics && selectedSubjectId && topics.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f1f3f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Topic Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Covered By</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Covered On</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Remarks</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {topics.map((t, index) => {
                  const isCompleted = t.status === "C";
                  const hasRemark = t.coveredDescription && t.coveredDescription.trim() !== "";

                  return (
                    <TableRow key={index} hover>
                      <TableCell>{t.topicOrder ?? "-"}</TableCell>

                      <TableCell sx={{ fontWeight: 500 }}>
                        {t.topicName || "—"}
                      </TableCell>

                      <TableCell>
                        {t.coveredByTeacherName || "—"}
                      </TableCell>

                      <TableCell>
                        {t.dateCovered || "—"}
                      </TableCell>

                      <TableCell>
                        {hasRemark ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewRemark(t.topicName, t.coveredDescription)}
                            sx={{
                              textTransform: "none",
                              borderColor: "#2c5364",
                              color: "#2c5364",
                              "&:hover": {
                                borderColor: "#0f2027",
                                bgcolor: "rgba(44, 83, 100, 0.08)",
                              },
                            }}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography color="text.secondary">—</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={isCompleted ? "Completed" : "Pending"}
                          color={isCompleted ? "success" : "error"}
                          sx={{
                            fontWeight: "bold",
                            minWidth: 100,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* NO TOPICS */}
        {!loadingTopics && selectedSubjectId && topics.length === 0 && (
          <Typography align="center" color="text.secondary" py={4}>
            Topics for this subject are not added yet.
          </Typography>
        )}
      </Paper>

      {/* ================= REMARK MODAL ================= */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(90deg, #0f2027, #203a43, #2c5364)",
            color: "white",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Topic Remarks
          </Typography>
          {/* <IconButton
            onClick={handleCloseModal}
            sx={{
              color: "white",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton> */}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Topic Name:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#0f2027" }}>
              {selectedRemark.topicName}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Remarks:
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f9f9f9",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {selectedRemark.remark || "No remarks available"}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              background: "linear-gradient(90deg, #0f2027, #203a43, #2c5364)",
              color: "white",
              textTransform: "none",
              px: 3,
              "&:hover": {
                background: "linear-gradient(90deg, #2c5364, #203a43, #0f2027)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentTopicProgress;
